import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
    OrderRouteService, RouteSearchOutputItemModel, RouteSearchOutputModel,
    ServerResponse
} from '@spa/api';
import {
    RouteAssociationFormControls,
    RouteAssociationFormControlsInterface, RouteFormControlsRouteArraySelector,
    RouteSetFormControlsRouteIsRequired
} from 'appAssets/descriptors';
import {CheckboxControl} from 'appShared/classes';

import {OrderRoutesAssociationEnum} from '../../enums';
import {RequiredValidatorKey} from '@spa/validators';
import {Subscription} from 'rxjs';

import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';

export class RouteAssociationForm {
    private orderRouteService: OrderRouteService;
    private readonly objectId: number;
    private readonly associationType: OrderRoutesAssociationEnum;
    private readonly brokerId: number;
    private readonly currencyCode: string;
    private initialRouteIds: number[];
    public availableRoutes: RouteSearchOutputModel;
    public formGroup: FormGroup;
    public controls: RouteAssociationFormControlsInterface;
    public routesErrors: string[];
    public subscriptions: Subscription[];
    public routeFormControlsRouteArraySelector: string;

    // tslint:disable-next-line:max-line-length
    constructor(
        orderRouteService: OrderRouteService,
        associationType: OrderRoutesAssociationEnum,
        objectId: number,
        availableRoutes: RouteSearchOutputModel,
        brokerId: number = null,
        currencyCode: string = null
    ) {
        this.objectId = objectId;
        this.associationType = associationType;
        this.availableRoutes = availableRoutes;
        this.orderRouteService = orderRouteService;
        this.subscriptions = [];
        this.routesErrors = [];
        this.routeFormControlsRouteArraySelector = RouteFormControlsRouteArraySelector;
        this.brokerId = brokerId;
        this.currencyCode = currencyCode;
        this.initialRouteIds = [];

    }

    private getSelectedRouteIds(): number[] {
        const routesFormControl = this.formGroup.get(this.routeFormControlsRouteArraySelector) as FormArray;

        return routesFormControl.value.filter((item: number) => item !== null);
    }

    private updateDirtyState(): void {
        if (this.hasChanges()) {
            this.formGroup.markAsDirty();

            return;
        }

        this.formGroup.markAsPristine();
    }

    private normalizeIds(ids: number[] = []): number[] {
        return ids.slice().sort((a: number, b: number) => a - b);
    }

    private areSameIds(first: number[], second: number[]): boolean {
        const normalizedFirst = this.normalizeIds(first);
        const normalizedSecond = this.normalizeIds(second);

        if (normalizedFirst.length !== normalizedSecond.length) {
            return false;
        }

        return normalizedFirst.every((id: number, index: number) => id === normalizedSecond[index]);
    }

    initForm(currentRoutes: number[] = []): void {
        this.formGroup = new FormGroup({});
        this.initialRouteIds = this.normalizeIds(currentRoutes);

        this.controls = RouteAssociationFormControls();
        const routeIdsFormArray = new FormArray([]);

        if (this.availableRoutes && this.availableRoutes.total_elements > 0) {
            this.controls.routes = this.availableRoutes.content.map((r: RouteSearchOutputItemModel, index: number) => {
                const checkControl = new CheckboxControl({
                    name: index + '',
                    value: r.getId(),
                    placeholder: r.getDisplayName(),
                    nonCapitalizedPlaceholder: true,
                    validators: [],
                    componentType: ComponentTypesEnum.Checkbox,
                    checked: this.initialRouteIds.indexOf(r.getId()) > -1,
                    color: ColorCodesEnum.Primary,
                    expectBooleanValue: false
                });
                const checkboxFormControl = new FormControl(checkControl.value, checkControl.validators);
                routeIdsFormArray.push(checkboxFormControl);

                return checkControl;
            });
        }
        this.formGroup.addControl(this.routeFormControlsRouteArraySelector, routeIdsFormArray);
        this.subscriptions.push(routeIdsFormArray.valueChanges.subscribe(() => this.updateDirtyState()));
    }

    createRequest(): ServerResponse<null> {
        if (!this.hasChanges()) {
            return null;
        }
        const routeIds = this.getSelectedRouteIds();
        switch (this.associationType){
            case OrderRoutesAssociationEnum.Account:
                return this.orderRouteService.addRoutesToAccount(this.objectId, routeIds);
            case OrderRoutesAssociationEnum.AccountGroup:
                return this.orderRouteService.addRoutesToAccountGroup(this.objectId, routeIds, this.brokerId, this.currencyCode);
            case OrderRoutesAssociationEnum.TradingPlan:
                return this.orderRouteService.addRoutesToPlan(this.objectId, routeIds);
        }
    }

    hasChanges(): boolean {
        return !this.areSameIds(this.initialRouteIds, this.getSelectedRouteIds());
    }

    validateForm(messagesFunction: (controlsLabel?: string) => { [key: string]: string }): boolean {
        let isInvalidForm = false;

        const routeIds = this.getSelectedRouteIds();
        this.routesErrors.splice(0, this.routesErrors.length);
        if (routeIds.length === 0) {
            this.routesErrors.push(messagesFunction(RouteSetFormControlsRouteIsRequired)[RequiredValidatorKey]);

            isInvalidForm = true;
        }

        return !isInvalidForm;
    }
}
